import cx from 'classnames';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import * as React from 'react';
import { isEmpty, keys, entries } from 'lodash';
import { graphql, compose } from 'react-apollo';
import CurrentUser from 'auth/CurrentUser';
import PAID_ORG_STATUS from 'user/queries/paidOrgStatusQuery.gql';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CharacterCount from 'lib/CharacterCount';
import Chat from 'entities/Chat';
import ChatActions from 'chat_v2/actions/ChatActions';
// @ts-ignore
import Composer from 'chat_v2/components/Composer';
// @ts-ignore
import ComposerFilePreview from 'chat_v2/components/ComposerFilePreview';
import ConfirmDialog from 'components/ConfirmDialog';
// @ts-ignore
import ConversationViewComposer from 'chat_v2/components/ConversationViewComposer';
import DateConstants from 'constants/DateConstants';
import DropZoneModal from 'components/DropZoneModal';
import Glyph from 'components/Glyph';
import Icon from 'components/Icon';
import Image from 'components/Image';
import KeyConstants from 'constants/KeyConstants';
// @ts-ignore
import LanguageHoverCard from 'chat_v2/components/LanguageHoverCard';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
// @ts-ignore
import MessageComposerActions from 'chat_v2/actions/MessageComposerActions';
import MessageComposerStore from 'chat_v2/stores/MessageComposerStore';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text';
// @ts-ignore
import Timing from 'lib/Timing';
import TranslationActions from 'actions/TranslationActions';
import { getLanguagePreviewTitle } from 'translations/utils';
import { TranslationPreview, TranslationState } from 'translations/components/TranslationPreview';
// @ts-ignore
import RemainingCharactersCount from 'chat_v2/components/FullPageComposer/RemainingCharactersCount';
import SMSPreviewButton from './SMSPreviewButton';

import { mountModal } from 'lib/modal';
// @ts-ignore
import ComposerAlert from './ComposerAlert';
// @ts-ignore
import withWidth from 'lib/withWidth';
import dateTimeFormat from 'lib/dateTimeFormat';
import errorLogger from 'utils/errorLogger';

import './styles/MessageComposer.scss';
import { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from 'translations/queries/__generated__/GetLanguageSet';
import { MessageTranslationsInput } from 'globalTypes';
import { detectLanguage, translatedMessage } from 'translations/actions';
import TranslateButton from 'translations/components/TranslateButton';
import UploadButton from 'components/UploadButton';
import { uploadFileRequest, V2File } from 'clients/api/fileUploads';
import { uploadFile as uploadFileHelper } from 'lib/fileUploadHelper';
import { TranslationType, EditedMessageTranslationsType } from 'translations/types';

const OFFICE_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const AFTER_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
export const CHAR_LIMIT = 1500;
export const SMS_PREVIEW_LIMIT = 175; // TODO check this number
const UI_CONTEXT = 'chat_composer';

type Props = {
  isNewChat?: boolean;
  chat: Chat;
  onMessageSend: (text: string, file?: V2File, translations?: MessageTranslationsInput) => void;
  width: number;
  openSideBar?: () => void;
  className?: string;
  userHasPaidOrg: boolean;
  /* dnd props */
  connectDropTarget: (el: React.ReactElement) => React.ReactElement;
  isDraggingFile?: boolean;
  languageCounts: LanguageCounts[];
  toggleTranslate: (shouldTranslate: boolean) => void;
};

type State = {
  composerError: boolean;
  latestTranslation: string;
  text: string;
  translationBoxOpened: boolean;
  shouldTranslate: boolean;
  translationState: TranslationState;
  editedTranslations: EditedMessageTranslationsType;
  translationsPreviewLanguage?: string;
  translationsPreviewActive: boolean;
  file?: V2File;
  fileData?: File;
  thumbnail?: string;
  uploadProgress?: number;
  uploadError?: Error;
  detectedLanguageStale: boolean;
  detectedMessageLanguage: string;
};

type monitorType = {
  getItem: () => { files: Array<File> };
  isOver: () => void;
};

type MessageComposerEventProperties = {
  language_code?: string;
};

const initialState: State = {
  composerError: false,
  latestTranslation: '',
  text: '',
  translationBoxOpened: false,
  shouldTranslate: false,
  translationState: TranslationState.None,
  translationsPreviewActive: false,
  detectedLanguageStale: false,
  detectedMessageLanguage: 'none',
  editedTranslations: {},
};

class MessageComposer extends React.Component<Props, State> {
  static MEDIUM = 750;
  static SMALL = 650;
  static XSMALL = 550;
  static XXSMALL = 450;

  state = {
    ...initialState,
    shouldTranslate: CurrentUser.getUser().getPreference('enabled_composer_translation'),
    detectedLanguageStale: CurrentUser.getUser().getPreference('enabled_composer_translation'),
  };

  componentWillMount() {
    const { chat } = this.props;

    if (chat.id) {
      const newState = MessageComposerStore.getComposerStates(chat.id);
      if (newState) {
        this.setState(newState);
      }
    }
  }

  componentWillUpdate(nextProps: Props) {
    const currentChatId = this.props.chat.id;
    const nextChatId = nextProps.chat.id;
    const chatChanged = nextChatId !== currentChatId;

    if (chatChanged) {
      // When switching chats, we save the composer state for persisted chats to be restored if we come back to this chat.
      if (!this.props.chat.isNew()) {
        const stateBeforeTimeout = this.state;
        // Timeout to place action at end of dispatch stack
        setTimeout(() => {
          MessageComposerActions.saveState(currentChatId, stateBeforeTimeout);
        }, 0);
      }

      if (this.refs.composer) {
        (this.refs.composer as any).clear();
      }

      if (nextChatId) {
        let newInitialState = {
          ...initialState,
          shouldTranslate: CurrentUser.getUser().getPreference('enabled_composer_translation'),
        };
        const state = MessageComposerStore.getComposerStates(nextChatId) || newInitialState;
        this.setState({ ...state });
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.chat.isNew()) {
      setTimeout(() => {
        MessageComposerActions.saveState(this.props.chat.id, this.state);
      }, 0);
    }
  }

  getDropzoneId = () => {
    if (this.props.isNewChat) {
      return 'newChat';
    } else {
      return this.props.chat.id;
    }
  };

  getTranslationBox = () => {
    if (!this.state.translationBoxOpened) {
      return null;
    }
    const languages = SettingsStore.getLanguages().primary;

    if (!languages) {
      return null;
    }

    const buttons = [];

    let displayedMax = keys(languages).length;
    let displayed = 0;

    if (this.props.width < MessageComposer.XXSMALL) {
      displayedMax -= 5;
    } else if (this.props.width < MessageComposer.XSMALL) {
      displayedMax -= 3;
    } else if (this.props.width < MessageComposer.SMALL) {
      displayedMax -= 2;
    } else if (this.props.width < MessageComposer.MEDIUM) {
      displayedMax -= 1;
    }

    for (const lang in languages) {
      displayed += 1;
      if (displayed <= displayedMax) {
        buttons.push(
          <Button
            key={`translate-${lang}`}
            eventContextName={UI_CONTEXT}
            eventElementName="translate_button"
            eventProperties={{
              language_code: lang,
            }}
            onClick={() => this.translateTo(lang)}>
            {languages[lang]}
          </Button>
        );
      }
    }

    buttons.push(
      <LayerTrigger
        key="show-more"
        layer={
          <LanguageHoverCard
            eventContextName={UI_CONTEXT}
            onLanguagePicked={(lang: string) => this.translateTo(lang)}
          />
        }
        placement="top"
        trigger="click"
        alignment="middle">
        <Button
          eventContextName={UI_CONTEXT}
          eventElementName="show_more_languages_button"
          level="secondary"
          color="graphite"
          size="xsmall"
          aria-label="More languages">
          <Glyph name="ellipses" solo />
        </Button>
      </LayerTrigger>
    );

    return (
      <div className="MessageComposer-translationBox">
        <div className="MessageComposer-translationBox-buttons">
          <Buttons color="graphite" group level="secondary" size="xsmall">
            {buttons}
          </Buttons>
        </div>

        <Button
          className="MessageComposer-translationBox-closeButton"
          eventContextName={UI_CONTEXT}
          eventElementName="close_translation_box"
          onClick={this.toggleTranslationBoxOpened}
          level="text"
          aria-label="Close">
          <Glyph name="close-circle" color="mascot" size="xlarge" solo />
        </Button>
      </div>
    );
  };

  toggleTranslationBoxOpened = () => {
    this.setState(({ translationBoxOpened }) => ({ translationBoxOpened: !translationBoxOpened }));
  };

  allowSendAfterHours: number | null = null;

  allowSendOutsideOfficeHours: { [key: string]: number } = {};

  chatState = {};

  handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      text: stateText,
      detectedLanguageStale,
      detectedMessageLanguage,
      editedTranslations,
    } = this.state;
    const text = event.target.value;

    if (text === stateText) {
      return;
    }

    // if the message has been cleared completed, clear all the translation data
    if (text.trim() === '') {
      this.setState({
        text,
        composerError: text.trim().length > CHAR_LIMIT,
        detectedLanguageStale: true,
        editedTranslations: {},
        translationState: TranslationState.Empty,
      });
    }
    // otherwise, clear the autotranslations, but not the edited ones
    else {
      const stillEditedAfterTextChange: EditedMessageTranslationsType = {};
      Object.keys(editedTranslations)
        .filter(lc => editedTranslations[lc].intent !== 'Autotranslated')
        .forEach(lc => {
          stillEditedAfterTextChange[lc] = editedTranslations[lc];
        });

      this.setState({
        text,
        composerError: text.trim().length > CHAR_LIMIT,
        detectedLanguageStale: true,
        editedTranslations: stillEditedAfterTextChange,
      });
    }
  };

  confirmSendMessageAfterHours = () => {
    const timeWarning = Timing.isEarly() ? 'It’s pretty early.' : 'It’s pretty late.';
    const question = 'Send this message anyway?';
    const title = (
      <div>
        <Image
          className="MessageComposer-confirmImage"
          src={require('assets/images/after-hours.svg')}
        />
        <p>
          {timeWarning} {question}
        </p>
      </div>
    );
    mountModal(
      <ConfirmDialog
        title={title}
        eventContext="ChatMessageComposer"
        size="xsmall"
        onConfirm={() => {
          this.allowSendAfterHours = window.setTimeout(() => {
            this.allowSendAfterHours = null;
          }, AFTER_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  getNextAvailability = () => {
    const { chat } = this.props;
    const otherChatMembers = chat.getOtherChatMembers();

    for (let i = 0; i < otherChatMembers.length; i++) {
      const member = otherChatMembers[i];
      const officeHours = member.getOfficeHours();
      if (!officeHours.isAvailableNow()) {
        return {
          otherChatMember: member,
          nextAvailability: officeHours.getNextAvailability(),
        };
      }
    }
    return {};
  };

  confirmSendMessageOutsideOfOfficeHours = () => {
    const { chat } = this.props;
    const { otherChatMember, nextAvailability } = this.getNextAvailability();

    if (isEmpty(nextAvailability)) {
      throw new Error('unable to determine nextAvailability');
    }

    const startTime = nextAvailability.getStartTime();
    const date = new Date();
    const diff = (nextAvailability.getDay() + 7 - date.getDay()) % 7;
    date.setHours(Math.floor(startTime / 3600));
    date.setMinutes((startTime % 3600) / 60);
    const day = diff === 1 ? 'tomorrow' : DateConstants.WEEKDAYS[nextAvailability.getDay()];
    const time = dateTimeFormat(date, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const otherChatMemberName = otherChatMember?.getName() ?? '???';
    mountModal(
      <ConfirmDialog
        size="xsmall"
        glyph="clock-bubble"
        glyphColor="pennant"
        title={`Uh-oh! ${otherChatMemberName} won’t be back until ${day} at ${time}.`}
        subtext={`Office hours for ${otherChatMemberName} won’t start until ${day} at ${time}, so they may not receive your message right now. Send anyway?`}
        onConfirm={() => {
          this.allowSendOutsideOfficeHours[chat.id] = window.setTimeout(() => {
            delete this.allowSendOutsideOfficeHours[chat.id];
          }, OFFICE_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  focus = () => {
    if (this.refs.composer) {
      (this.refs.composer as ConversationViewComposer).focus();
    }
  };

  handleEnableRepliesButtonClick = (event: Event) => {
    event.preventDefault();
    ChatActions.enableReplies(this.props.chat);
  };

  isChatEnabled = () => {
    return this.props.chat.isPresent() && !this.props.chat.isUnsubscribed();
  };

  isValidMessage = () => {
    const { text, file, fileData, uploadProgress } = this.state;

    if (fileData) {
      return uploadProgress === 100 && file;
    }

    return text.trim().length > 0;
  };

  getPlaceholder = () => {
    const chatTitle = this.props.chat.getTitle();
    if (chatTitle) {
      return `Chat with ${chatTitle}`;
    } else {
      return 'Type a message';
    }
  };

  getPredefinedTranslations = (editedTranslations: EditedMessageTranslationsType) => {
    if (isEmpty(editedTranslations)) return null;

    return entries(editedTranslations)
      .filter(([_lc, { intent }]) => intent == 'Off' || intent == 'Edited')
      .map(([languageCode, { intent, userEditedTranslation }]) => {
        return {
          language_code: languageCode,
          translation_intent: intent.toLowerCase(),
          translated_text: userEditedTranslation,
        };
      });
  };

  onMessageSend = (text: string, file?: V2File, translations?: MessageTranslationsInput) => {
    this.props.onMessageSend(this.state.text, file, translations);
    this.setState({ editedTranslations: {} });
  };

  sendMessage = () => {
    const { chat, languageCounts } = this.props;
    const {
      shouldTranslate,
      file,
      text,
      editedTranslations,
      detectedLanguageStale,
      detectedMessageLanguage,
    } = this.state;
    if (chat.isOutsideOfOtherOfficeHours() && !this.allowSendOutsideOfficeHours[chat.id]) {
      this.confirmSendMessageOutsideOfOfficeHours();
    } else if ((Timing.isLate() || Timing.isEarly()) && !this.allowSendAfterHours) {
      this.confirmSendMessageAfterHours();
    } else {
      let translations = {};
      if (shouldTranslate) {
        const targetLanguageCodes = languageCounts.map(lc => lc.languageCode);
        const predefinedTranslations = this.getPredefinedTranslations(editedTranslations);
        const originalLanguageCode =
          !detectedLanguageStale && detectedMessageLanguage != 'none'
            ? detectedMessageLanguage
            : null;

        translations = {
          targetLanguageCodes,
          predefinedTranslations,
          originalLanguageCode,
        };
      }
      this.onMessageSend(text, file, translations);
      (this.refs.composer as any).clear();
      this.setState({ text: '' });
      this.resetFileState();
    }
  };

  handleReturn = (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (event.keyCode === KeyConstants.ENTER && !event.shiftKey) {
      this.handleSubmit(event);
      return true;
    }
    return false;
  };

  handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    this.submitComposer();
  };

  submitComposer = () => {
    if (!this.submitDisabled()) {
      this.sendMessage();
    }
  };

  submitDisabled = () => {
    return (
      this.state.translationBoxOpened ||
      this.state.composerError ||
      !this.isChatEnabled() ||
      !this.isValidMessage()
    );
  };

  translateTo = (lang: string) => {
    const { text } = this.state;
    TranslationActions.translate(text, lang)
      .then(response => {
        this.setState({
          text: response.translation,
          latestTranslation: lang,
          translationBoxOpened: false,
        });
      })
      .catch(errorLogger('TranslationActions.translate failed'));
  };

  renderSendButton = (eventProperties: MessageComposerEventProperties) => {
    return (
      <Button
        data-test="send-button"
        data-test-selector="message-composer-send-button"
        eventContextName={UI_CONTEXT}
        eventElementName="send_button"
        eventProperties={eventProperties}
        level="primary"
        size="xxsmall"
        onClick={this.handleSubmit}
        disabled={this.submitDisabled()}>
        Send
      </Button>
    );
  };

  toggleTranslate = (shouldTranslate: boolean) => {
    this.setState({
      shouldTranslate,
      detectedLanguageStale: shouldTranslate,
      editedTranslations: {},
    });
    CurrentUser.getUser().savePreference('enabled_composer_translation', shouldTranslate);
    this.detectMessageLanguage();
  };

  userHasPreferredLanguageV2Feature = () =>
    CurrentUser.getUser().hasFeature('2021.aug.preferred_language_translation_v2');

  detectMessageLanguage = () => {
    if (!this.userHasPreferredLanguageV2Feature()) {
      return;
    }

    const { detectedLanguageStale, text } = this.state;

    if (text.trim() === '') {
      this.updateDetectedMessageLanguage('none');
      return;
    }

    if (detectedLanguageStale) {
      detectLanguage(text).then(resp => {
        if (resp && resp.success) {
          this.updateDetectedMessageLanguage(resp.languageCode);
        }
      });
    }
  };

  noPreferenceComposerText = (noPreferenceCount: number) => {
    return (
      noPreferenceCount +
      (noPreferenceCount > 1 ? " recipients haven't" : " recipient hasn't") +
      " set a preferred language and will receive the original message. To change someone's preferred language, edit their profile from the people list."
    );
  };

  getCurrentTextTranslation = (languageCode?: string) => {
    const { text } = this.state;
    const { languageCounts } = this.props;
    if (!languageCode) {
      return null;
    }
    if (languageCode === 'none') {
      const noPreference = languageCounts.filter(lc => lc.languageCode === 'none')[0];
      return this.noPreferenceComposerText(noPreference.count);
    }
    const translationData = this.state.editedTranslations[languageCode];
    if (!translationData) {
      return null;
    }
    switch (translationData.intent) {
      case 'Autotranslated':
        return translationData.autoTranslation;

      case 'Edited':
        return translationData.userEditedTranslation || '';

      case 'Off':
        return text;

      default:
        return null;
    }
  };

  updateDetectedMessageLanguage = (languageCode?: string | null | undefined) => {
    if (!languageCode) return;

    this.setState({
      detectedMessageLanguage: languageCode,
      detectedLanguageStale: false,
    });
  };

  getCharactersRemaining = () => {
    const { text, file } = this.state;
    return CharacterCount.getRemainingCharactersForText(
      CHAR_LIMIT,
      CHAR_LIMIT, // TODO change to charLimitWithFiles
      text.trim(),
      file !== undefined
    );
  };

  retrieveAndPreviewAutotranslation = (languageCode: string) => {
    const { text, detectedLanguageStale, detectedMessageLanguage, editedTranslations } = this.state;
    const { userEditedTranslation } = editedTranslations[languageCode] || {};

    const updateForTranslationState = (translationState: TranslationState) => {
      this.setState({
        translationState,
        translationsPreviewActive: true,
        translationsPreviewLanguage: languageCode,
      });
    };

    if (this.getCharactersRemaining() < 0) {
      updateForTranslationState(TranslationState.LimitError);
      return;
    }

    if (text.trim() === '') {
      updateForTranslationState(TranslationState.Empty);
      return;
    } else {
      updateForTranslationState(TranslationState.Loading);
    }

    const translatedMessageResponse = translatedMessage({
      languageCode: detectedLanguageStale ? 'none' : detectedMessageLanguage,
      targetLanguageCode: languageCode,
      text,
    });
    translatedMessageResponse
      .then(response => {
        if (response.success) {
          const { detectedLanguageCode, translatedText } = response;
          this.saveUpdatedTranslation(languageCode, {
            intent: 'Autotranslated',
            autoTranslation: translatedText,
            userEditedTranslation,
            originalText: text,
          });
          this.updateDetectedMessageLanguage(detectedLanguageCode);
          updateForTranslationState(TranslationState.Success);
        } else {
          updateForTranslationState(TranslationState.ServerError);
        }
      })
      .catch(error => {
        errorLogger(error);
        updateForTranslationState(TranslationState.ServerError);
      });
  };

  displayTranslatedMessagePreview = (languageCode: string) => {
    const { languageCounts } = this.props;
    let translationData = this.state.editedTranslations[languageCode];

    // don't even bother apollo if a preference isn't set
    if (languageCode === 'none') {
      const noPreference = languageCounts.filter(lc => lc.languageCode === 'none')[0];
      translationData = {
        intent: 'Off',
        autoTranslation: this.noPreferenceComposerText(noPreference.count),
        originalText: this.state.text,
      } as TranslationType;
      this.saveUpdatedTranslation('none', translationData);
    }

    // if we have already done a translation for this, use that
    if (translationData) {
      this.setState({
        translationsPreviewActive: true,
        translationsPreviewLanguage: languageCode,
        translationState: translationData.originalText.trim() === '' ? TranslationState.Empty : TranslationState.Success,
      });
      return;
    }

    this.retrieveAndPreviewAutotranslation(languageCode);
  };

  backToOriginal = () => {
    this.setState({
      translationsPreviewActive: false,
    });
  };

  saveUpdatedTranslation = (languageCode: string, translation: TranslationType) => {
    const editedTranslationsState = this.state.editedTranslations;
    editedTranslationsState[languageCode] = translation as TranslationType;

    this.setState({
      editedTranslations: editedTranslationsState,
    });
  };

  renderMessageComposer = () => {
    const {
      composerError,
      translationBoxOpened,
      latestTranslation,
      text,
      file,
      fileData,
      uploadProgress,
      thumbnail,
      uploadError,
      translationState,
    } = this.state;
    const { chat, isDraggingFile, userHasPaidOrg } = this.props;

    const currentUser = CurrentUser.getUser();
    const longerMessagesEnabled = currentUser.hasFeature('2020.sep.longer_messages.chat');

    if (!chat.canSend() || chat.isInsufficientlyConnected()) {
      return null;
    }

    const attachButton = (
      <UploadButton
        className="MessageComposer-attachIconButton"
        data-test="attach-button"
        level="icon"
        fileContextKey=""
        disabled={fileData !== undefined}
        uploadRequest={(fileContextKey: string, fileKey: string, data: File) =>
          this.uploadFile(data)
        }
        loadThumbnail={(fileContextKey: string, filename: string, thumbnail: string) => {
          this.setState({ thumbnail });
        }}>
        <Text size="xlarge">
          <Icon name="paperclip-stroked" color="chalkboard" size="inherit" />
        </Text>
      </UploadButton>
    );

    const translateButton = () => {
      const {
        shouldTranslate,
        text,
        translationsPreviewActive,
        editedTranslations,
        detectedMessageLanguage,
      } = this.state;
      const { languageCounts } = this.props;
      return (
        <TranslateButton
          shouldTranslate={shouldTranslate}
          languageCounts={languageCounts}
          data-test="ff-translate-button"
          toggleTranslate={this.toggleTranslate}
          onLangChange={(lang: string) => this.setState({ latestTranslation: lang })}
          dataTestSelector="message_composer_translation_button"
          textToTranslate={text}
          displayTranslatedMessagePreview={this.displayTranslatedMessagePreview}
          backToOriginalCallback={this.backToOriginal}
          translationsPreviewActive={translationsPreviewActive}
          isDisabled={false}
          editedTranslations={editedTranslations}
          detectedMessageLanguage={detectedMessageLanguage}
        />
      );
    };
    const numCharactersRemaining = this.getCharactersRemaining();
    const remainingCharactersCountComponent = (
      <div className="MessageComposer-remainingCharacterCount">
        <RemainingCharactersCount count={numCharactersRemaining} />
      </div>
    );

    const filePreview =
      fileData !== undefined ? (
        <div className="MessageComposer-preview">
          <ComposerFilePreview
            className={cx({
              draggingFile: isDraggingFile,
            })}
            progress={uploadProgress}
            thumbnail={thumbnail}
            type={file?.content_type}
            error={uploadError !== undefined}
            onDelete={this.resetFileState}
            onRetry={() => this.uploadFile(fileData)}
          />
        </div>
      ) : null;

    const eventProperties: MessageComposerEventProperties = {};
    if (latestTranslation) {
      eventProperties.language_code = latestTranslation;
    }

    const otherMembersPlaceholderText = this.getPlaceholder();

    const {
      translationsPreviewActive,
      translationsPreviewLanguage,
      editedTranslations,
    } = this.state;

    let previewTranslation: TranslationType | undefined;
    if (translationsPreviewLanguage) {
      previewTranslation = editedTranslations[translationsPreviewLanguage] || {
        intent: 'Autotranslated',
        originalText: text,
      };
    }

    const composerValue = translationsPreviewActive
      ? this.getCurrentTextTranslation(translationsPreviewLanguage)
      : text;

    return (
      <div className="MessageComposer-composerContainer" data-test-selector="message-composer">
        <MediaObject figure="right" gutter="xsmall">
          <div>
            {translationsPreviewActive &&
            previewTranslation &&
            translationsPreviewLanguage ? (
              <TranslationPreview
                canEdit={userHasPaidOrg && this.userHasPreferredLanguageV2Feature()}
                filePreview={filePreview}
                onClose={this.backToOriginal}
                languageCode={translationsPreviewLanguage}
                textBeforeTranslation={text}
                currentTranslation={previewTranslation}
                translationState={translationState}
                handleTranslationChange={this.saveUpdatedTranslation}
                getUpdatedTranslation={this.retrieveAndPreviewAutotranslation}
              />
            ) : (
              <Composer
                autogrow
                autoFocus
                constrainMaxHeight
                ref="composer"
                className="MessageComposer-composer"
                onChange={this.handleTextChange}
                onReturnKeyPress={this.handleReturn}
                onBlur={this.detectMessageLanguage}
                error={composerError}
                value={composerValue}
                placeholder={otherMembersPlaceholderText}
                showTitle={translationsPreviewActive}
                title={getLanguagePreviewTitle(translationsPreviewLanguage || '')}
                hideTitleAction={this.backToOriginal}
                disabled={!this.isChatEnabled() || translationsPreviewActive}>
                {filePreview}
                {this.getTranslationBox()}
              </Composer>
            )}
            <div className="MessageComposer-actions">
              <Buttons margin="small">
                {!translationsPreviewActive && attachButton}
                {translateButton()}
                {longerMessagesEnabled && (
                  <SMSPreviewButton
                    text={text}
                    sms_preview_limit={SMS_PREVIEW_LIMIT}
                    data-test="sms-preview-button"
                  />
                )}
              </Buttons>
              <Buttons margin="medium">
                {remainingCharactersCountComponent}
                {!translationsPreviewActive && this.renderSendButton(eventProperties)}
              </Buttons>
            </div>
          </div>
          <div />
        </MediaObject>
      </div>
    );
  };

  render() {
    const { chat, className, connectDropTarget, isDraggingFile, openSideBar } = this.props;

    const { file } = this.state;

    const dropzone = (
      <div>
        <DropZoneModal
          titleDrop="Drop a file here"
          isFileUploaded={file !== undefined}
          isHovering
        />
      </div>
    );
    const composerAlert = <ComposerAlert chat={chat} openSideBar={openSideBar} />;
    return connectDropTarget(
      <div className={cx('MessageComposer', className)}>
        {isDraggingFile && dropzone}
        {composerAlert}
        {this.renderMessageComposer()}
      </div>
    );
  }

  uploadFile = (fileData: File) => {
    this.setState({ fileData });

    uploadFileRequest(fileData, false, (percent: number) => {
      if (this.state.fileData) {
        this.setState({ uploadProgress: percent });
      }
    })
      .then(response => {
        if (this.state.fileData) {
          this.setState({ file: response });
        }
      })
      .catch(err => this.setState({ uploadError: err }));
  };

  resetFileState = () => {
    this.setState({
      file: undefined,
      fileData: undefined,
      thumbnail: undefined,
      uploadError: undefined,
      uploadProgress: undefined,
    });
  };
}

const dndEvents = {
  drop: (props: Props, monitor: monitorType, component: MessageComposer) => {
    uploadFileHelper(
      '',
      monitor.getItem().files[0],
      (fileContextKey: string, fileKey: string, data: File) => component.uploadFile(data),
      (fileContextKey: string, filename: string, newThumbnail: string) =>
        component.setState({ thumbnail: newThumbnail })
    );
  },
};

const collect = (connector: { dropTarget: () => void }, monitor: monitorType) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const type = NativeTypes.FILE;

const withPaidStatus = graphql(PAID_ORG_STATUS, {
  props: ({ data }) => {
    const foundPaidOrg = (userData: any) => {
      return (
        !userData?.me?.organizations?.every((o: any) => !o.paid) ||
        !userData?.me?.classes?.every((c: any) => !c.organization || !c.organization.paid)
      );
    };
    const hasPaidOrg = foundPaidOrg(data);

    return { userHasPaidOrg: hasPaidOrg };
  },
});

export { MessageComposer };

export default compose(
  withWidth,
  withPaidStatus,
  DropTarget(type, dndEvents, collect)
)(MessageComposer);

export const CHARACTER_LIMIT = CHAR_LIMIT;
